// React component

import { motion, useMotionTemplate, useMotionValue } from "framer-motion";
import React, { useCallback, useEffect, useRef } from "react";

import { cn } from "@/lib";

interface MagicCardProps extends React.HTMLAttributes<HTMLDivElement> {
  gradientSize?: number;
  gradientColor?: string;
  gradientOpacity?: number;
  gradientFrom?: string;
  gradientTo?: string;
}

export function MagicCard({
  children,
  className,
  gradientSize = 220,
  gradientColor = "rgba(59,130,246,0.35)",
  gradientOpacity = 0.9,
  gradientFrom = "#2563eb",
  gradientTo = "#a855f7",
}: MagicCardProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const mouseX = useMotionValue(-gradientSize);
  const mouseY = useMotionValue(-gradientSize);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (cardRef.current) {
        const { left, top } = cardRef.current.getBoundingClientRect();
        const clientX = e.clientX;
        const clientY = e.clientY;
        mouseX.set(clientX - left);
        mouseY.set(clientY - top);
      }
    },
    [mouseX, mouseY],
  );

  const handleMouseOut = useCallback(
    (e: MouseEvent) => {
      if (!e.relatedTarget) {
        document.removeEventListener("mousemove", handleMouseMove);
        mouseX.set(-gradientSize);
        mouseY.set(-gradientSize);
      }
    },
    [handleMouseMove, mouseX, gradientSize, mouseY],
  );

  const handleMouseEnter = useCallback(() => {
    document.addEventListener("mousemove", handleMouseMove);
    mouseX.set(-gradientSize);
    mouseY.set(-gradientSize);
  }, [handleMouseMove, mouseX, gradientSize, mouseY]);

  useEffect(() => {
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseout", handleMouseOut);
    document.addEventListener("mouseenter", handleMouseEnter);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseout", handleMouseOut);
      document.removeEventListener("mouseenter", handleMouseEnter);
    };
  }, [handleMouseEnter, handleMouseMove, handleMouseOut]);

  useEffect(() => {
    mouseX.set(-gradientSize);
    mouseY.set(-gradientSize);
  }, [gradientSize, mouseX, mouseY]);

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative flex size-full overflow-hidden rounded-3xl border border-white/5 bg-[radial-gradient(circle_at_top,_rgba(18,24,40,0.95),_rgba(2,6,23,0.9))] shadow-[0_35px_70px_rgba(2,6,23,0.9)] backdrop-blur-2xl transition-all duration-500 hover:border-white/15 hover:shadow-[0_45px_90px_rgba(2,6,23,1)]",
        className,
      )}
      data-oid="3l:um76"
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-[inherit] bg-[radial-gradient(circle_at_top,_rgba(59,130,246,0.25),_transparent_55%)] opacity-60"
        data-oid="zo.xx69"
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-[inherit] mix-blend-screen opacity-40 bg-[conic-gradient(from_120deg_at_50%_0%,rgba(59,130,246,0.35),rgba(147,51,234,0.35),transparent_65%)]"
        data-oid="s4iyv6k"
      />
      <div
        className="pointer-events-none absolute inset-0 rounded-[inherit] border border-white/10 opacity-60"
        data-oid="d1h19w9"
      />
      <div className="relative z-30 w-full" data-oid="0xw.0az">
        {children}
      </div>
      <motion.div
        className="pointer-events-none absolute inset-0 z-20 rounded-[inherit] opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px, ${gradientColor}, transparent 75%)
          `,
          opacity: gradientOpacity,
        }}
        data-oid="pgn:b5x"
      />

      <motion.div
        className="pointer-events-none absolute inset-0 z-10 rounded-[inherit] opacity-70 transition-opacity duration-500"
        style={{
          background: useMotionTemplate`
            radial-gradient(${gradientSize}px circle at ${mouseX}px ${mouseY}px,
              ${gradientFrom}, 
              ${gradientTo}, 
              rgba(15,23,42,0.6) 100%
            )
          `,
        }}
        data-oid="h7u5q29"
      />
      <div
        className="pointer-events-none absolute inset-[1px] rounded-[inherit] border border-white/5 opacity-80"
        data-oid="2lgzte."
      />
    </div>
  );
}
